/* eslint no-empty: ["error", { "allowEmptyCatch": true }] */

import React from 'react';
import DatePicker from 'react-datepicker';
import { ChevronDown } from 'react-feather';
import moment from 'moment';
import { isEqual } from 'lodash';
import classNames from 'classnames';
import DateFormat from 'constants/date_format';
import { capitalize } from 'utils/string_utils';
import Popover from '../../popover';
import ApplyButton from './apply_button';

/**
 * @prop {string} label (required)
 * @prop {object} dateFormat
 * @prop {string} dateFormat.input (required)
 * @prop {string} dateFormat.output (required)
 * @prop {object} data
 * @prop {string} data.input
 * @prop {string} data.output
 * @prop {func} applyCallback (required)
 */

class DatePickerFilter extends React.Component {
  constructor(props) {
    super(props);

    const { startDate, endDate } = props;

    this.state = {
      isActive: false,
      shouldApplyFilter: false,
      isDatePickerOpen: false,
      startDate: startDate ? moment(startDate) : null,
      endDate: endDate ? moment(endDate) : null,
      lastSelectedOptions: props.data
        ? props.data
        : { startDate: null, endDate: null },
    };

    this.callApplyCallback = this.callApplyCallback.bind(this);
    this.handleDatePickerOpen = this.handleDatePickerOpen.bind(this);
    this.handleDatePickerClose = this.handleDatePickerClose.bind(this);
  }

  UNSAFE_componentWillReceiveProps({ startDate = null, endDate = null }) {
    this.setState({
      startDate: startDate ? moment(startDate) : startDate,
      endDate: endDate ? moment(endDate) : endDate,
      lastSelectedOptions: {
        startDate: this.props.startDate,
        endDate: this.props.endDate,
      },
    });
  }

  getLabel() {
    const { endDate, startDate } = this.state;
    const { label, longLabel } = this.props;
    let range;
    if (!startDate && !endDate) {
      return longLabel;
    }
    if (startDate && !endDate) {
      range = `${t('common.from')} ${moment(startDate).format('MM/DD')}`;
    }
    if (!startDate && endDate) {
      range = `${t('common.to')} ${moment(endDate).format('MM/DD')}`;
    }
    if (startDate && endDate) {
      range = `${moment(startDate).format('MM/DD')} – ${moment(endDate).format(
        'MM/DD',
      )}`;
    }
    return `${label} (${range})`;
  }

  getParsedOptions({ startDate, endDate }) {
    // Change the moment locale stored on the date object so it is formatted
    // YYYY-MM-DD in the params and URL, but still displayed localized.
    return {
      start: startDate
        ? startDate.locale('en').format(this.props.dateFormat.output)
        : null,
      end: endDate
        ? endDate.locale('en').format(this.props.dateFormat.output)
        : null,
    };
  }

  handleDatePickerOpen() {
    this.setState({ isDatePickerOpen: true });
  }

  handleDatePickerClose() {
    this.setState({ isDatePickerOpen: false });
  }

  handleDateChange(
    { startDate = this.state.startDate, endDate = this.state.endDate },
    callback,
  ) {
    if (!!startDate && !!endDate && startDate.isAfter(endDate)) {
      const temp = startDate;
      startDate = endDate;
      endDate = temp;
    }
    this.setState({ startDate, endDate }, callback);
  }

  handleStartDateChange(startDate) {
    this.handleDateChange({ startDate }, () => {
      this.setState({ shouldApplyFilter: this.shouldApplyFilter() });
    });
  }

  handleEndDateChange(endDate) {
    this.handleDateChange({ endDate }, () => {
      this.setState({ shouldApplyFilter: this.shouldApplyFilter() });
    });
  }

  showMenu = () => {
    this.setState({ isActive: true });
  };

  closeMenu() {
    if (!this.state.isDatePickerOpen) this.setState({ isActive: false });
  }

  /* Apply/Callback Handlers */

  callApplyCallback() {
    const { startDate, endDate, shouldApplyFilter } = this.state;
    const currentOptions = this.getParsedOptions({ startDate, endDate });
    if (shouldApplyFilter) {
      this.handleDatePickerClose();
      this.setState(
        {
          shouldApplyFilter: false,
          lastSelectedOptions: currentOptions,
        },
        () => {
          this.props.applyCallback(currentOptions);
          this.closeMenu();
        },
      );
    }
  }

  shouldApplyFilter() {
    const { startDate, endDate, lastSelectedOptions } = this.state;
    const currentOptions = this.getParsedOptions({ startDate, endDate });
    return (
      (!!startDate || !!endDate) &&
      !isEqual(currentOptions, lastSelectedOptions)
    );
  }

  renderToggleButton() {
    const { isActive } = this.state;
    const {
      renderToggle: customToggle,
      whiteTheme,
      accessible = false,
      featherIcon = false,
      pendoClassName,
    } = this.props;
    if (customToggle) {
      return customToggle({ isActive, toggle: this.showMenu });
    }
    const toggleButtonClassName = classNames({
      NewButton: !accessible,
      AccessibleSelect: accessible,
      'NewButton--active': isActive,
      'NewButton--white': whiteTheme,
      'NewButton--darkNavy': !whiteTheme,
      'd-f jc-sb ai-c pr-4': featherIcon,
    });
    return (
      <button
        onMouseDown={e => {
          // Prevents mouse click focus ring
          e.preventDefault();
        }}
        type="button"
        className={classNames(toggleButtonClassName, pendoClassName)}
        onClick={this.showMenu}
      >
        {this.getLabel()}
        {featherIcon && <ChevronDown className="" size={18} />}
      </button>
    );
  }

  render() {
    const { shouldApplyFilter, isActive, startDate, endDate } = this.state;
    const { dateFormat, wrapperClassName, popoverClassName } = this.props;

    return (
      <div className={wrapperClassName}>
        {this.renderToggleButton()}
        {isActive && (
          <Popover
            styleName={popoverClassName}
            dismissCallback={() => this.closeMenu()}
          >
            <div className="IndexFilter-dateInputs">
              <div className="IndexFilter-dateRange">
                <label htmlFor="startDate">{t('common.from')}</label>
                <DatePicker
                  id="startDate"
                  selected={startDate}
                  className="NewInput NewInput--lightestGray"
                  dateFormat={dateFormat.input}
                  onChange={date => this.handleStartDateChange(date)}
                  placeholderText={dateFormat.input}
                  onFocus={this.handleDatePickerOpen}
                  onBlur={this.handleDatePickerClose}
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
              <div className="IndexFilter-dateRange" htmlFor="endDate">
                <label htmlFor="endDate">{capitalize(t('common.to'))}</label>
                <DatePicker
                  id="endDate"
                  selected={endDate}
                  className="NewInput NewInput--lightestGray"
                  dateFormat={dateFormat.input}
                  onChange={date => this.handleEndDateChange(date)}
                  placeholderText={dateFormat.input}
                  onFocus={this.handleDatePickerOpen}
                  onBlur={this.handleDatePickerClose}
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
            </div>
            <ApplyButton
              onClick={this.callApplyCallback}
              enabled={shouldApplyFilter}
              accessible
            />
          </Popover>
        )}
      </div>
    );
  }
}

DatePickerFilter.defaultProps = {
  wrapperClassName: 'IndexFilter IndexFilter-button',
  popoverClassName: 'IndexFilter-popover',
  label: t('common.date'),
  longLabel: 'Date',
  dateFormat: {
    input: DateFormat.fixed.SPREADSHEET_DATE,
    output: DateFormat.fixed.DB_DATE,
  },
  startDate: null,
  endDate: null,
  whiteTheme: false,
};

export default DatePickerFilter;
